/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// RobotDatabase singleton class
// Responsible for managing robot
// sessions (collecting and storing info)
// Communicating with the analytics db
//
// (C) Robotical 2023
//
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import randomHashGenerator from "../helpers/randomHashGenerator";
import { sessionTitleUrlBuilder, updateInstanceUrlBuilder, warningInstanceUrlBuilder, } from "../path-builders/robot-path-builder";
import { Database } from "./Database";
export default class RobotDatabase extends Database {
    constructor(config, databaseName) {
        super(config, databaseName);
        this.dataToStore = {
            firstTimestamp: "",
            lastTimestamp: "",
            serialNo: "",
            systemVersion: "",
            sessionId: "",
            appSessionId: "",
            timezone: "",
        };
        this.heartbeatInterval = 60000;
    }
    getSessionsBySerialNo(serialNo) {
        return this.filterByProperty("serialNo", serialNo);
    }
    addDataToSession(key, value) {
        this.dataToStore[key] = value;
    }
    async setHeartbeat() {
        // every this.heartbeatInterval we sent a new timestamp
        // to the db. In that way we can estimate the time
        // marty was used.
        if (!this.heartbeat) {
            this.heartbeat = setInterval(async () => {
                const dbPath = sessionTitleUrlBuilder(this.dataToStore.sessionId);
                const exists = await this.exists(dbPath);
                if (exists) {
                    await this.update({ lastTimestamp: new Date().toISOString() }, dbPath);
                }
            }, this.heartbeatInterval);
        }
    }
    stopHeartbeat() {
        // stopping repeatedly sending msgs to the db
        if (this.heartbeat) {
            clearInterval(this.heartbeat);
        }
        this.heartbeat = undefined;
    }
    async addUpdateInstance(instanceType, title, msg, errorType, fwUpdateCheckCount) {
        // if sessionId is not defined then cancel
        if (!this.dataToStore.sessionId || !this.dataToStore.serialNo)
            return;
        // update instance can be either an update faliure or update success
        const instance = new UpdateInstance(instanceType, title, msg, errorType, fwUpdateCheckCount);
        const dbPath = updateInstanceUrlBuilder(this.dataToStore.sessionId, instance.instanceType);
        await this.set(instance, dbPath);
    }
    async addWarningMessage(messageObj, motorElem, hasSerialNumWarranty) {
        // if sessionId is not defined then cancel
        if (!this.dataToStore.sessionId || !this.dataToStore.serialNo)
            return;
        const warningMessage = new WarningMessageInstance(messageObj, motorElem, hasSerialNumWarranty);
        const dbPath = warningInstanceUrlBuilder(this.dataToStore.sessionId, warningMessage.title);
        await this.set(warningMessage, dbPath);
    }
    async startSession(serialNumber, systemVersion, appSessionId, ricRevision) {
        if (!this.db)
            await this.initialize();
        if (!serialNumber)
            serialNumber = "";
        this.addDataToSession("serialNo", serialNumber);
        this.addDataToSession("systemVersion", systemVersion);
        this.addDataToSession("appSessionId", appSessionId);
        this.addDataToSession("firstTimestamp", new Date().toISOString());
        this.addDataToSession("lastTimestamp", new Date().toISOString());
        this.addDataToSession("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
        this.addDataToSession("ricRev", ricRevision);
        // A marty was just successfully connected to the app. Starting session
        this.dataToStore.sessionId = randomHashGenerator(10);
        // safety net: making sure we only post when
        // the serial number is defined
        if (this.dataToStore.serialNo) {
            // sending the initial data. when the initial data are sent
            // we set up a heartbeat
            const res = await this.set(this.dataToStore, sessionTitleUrlBuilder(this.dataToStore.sessionId));
            if (res) {
                this.setHeartbeat();
            }
        }
    }
}
export class WarningMessageInstance {
    constructor(messageObj, motorElem, hasSerialNumWarranty) {
        this.type = messageObj.msgType;
        this.title = messageObj.msgBody;
        this.idNo = messageObj.IDNo;
        this.timestamp = new Date().toISOString();
        this.motorName = motorElem === null || motorElem === void 0 ? void 0 : motorElem.name;
        this.motortype = motorElem === null || motorElem === void 0 ? void 0 : motorElem.type;
        this.motorBusName = motorElem === null || motorElem === void 0 ? void 0 : motorElem.busName;
        this.motorAddr = motorElem === null || motorElem === void 0 ? void 0 : motorElem.addr;
        this.motorAddrValid = motorElem === null || motorElem === void 0 ? void 0 : motorElem.addrValid;
        this.motorIDNo = motorElem === null || motorElem === void 0 ? void 0 : motorElem.IDNo;
        this.motorWhoAmI = motorElem === null || motorElem === void 0 ? void 0 : motorElem.whoAmI;
        this.motorWhoAmITypeCode = motorElem === null || motorElem === void 0 ? void 0 : motorElem.whoAmITypeCode;
        this.motorSN = motorElem === null || motorElem === void 0 ? void 0 : motorElem.SN;
        this.motorVersionStr = motorElem === null || motorElem === void 0 ? void 0 : motorElem.versionStr;
        this.motorCommsOk = motorElem === null || motorElem === void 0 ? void 0 : motorElem.commsOk;
        this.hasSerialNumWarranty = hasSerialNumWarranty;
    }
}
// disruptive error means that the update didn't fail due to that error
// but perhaps some file/firmware wasn't updated properly
// prohibiting error means that something went wrong and
// nothing was updated
export class UpdateInstance {
    constructor(instanceType, title, msg, errorType, fwUpdateCheckCount) {
        this.instanceType = instanceType;
        this.errorType = errorType;
        this.title = title;
        this.msg = msg;
        this.timestamp = new Date().toISOString();
        this.fwUpdateCheckCount = fwUpdateCheckCount;
    }
}
